import Api from "../../api/Api";
import { toast } from "react-toastify";
import {
  ADD_IDS_SUCCESS,
  DELETE_IDS_SUCCESS,
  GET_IDS_SUCCESS,
  UPDATE_IDS_SUCCESS,
} from "./actionTypes";

const getListParams = (params, state) => ({
  page: params.page ?? state?.pagination?.page ?? 1,
  per_page: params.per_page ?? state?.pagination?.per_page ?? 10,
  search: params.search ?? state?.pagination?.search ?? "",
  scope: params.scope ?? "list",
  silent: params.silent ?? false,
});

const getResponseBody = (response) => response?.data ?? response;

const toList = (value) => (Array.isArray(value) ? value : []);

const buildSuccessPayload = (response, { search, scope }) => {
  const data = getResponseBody(response);
  const items = toList(data.data);

  const payload = {
    total: data.total ?? items.length,
    page: data.page ?? 1,
    per_page: data.per_page ?? 10,
    search,
    scope,
  };

  if (scope === "defective") {
    payload.defective = items;
  } else if (scope === "exports") {
    payload.exports = items;
  } else {
    payload.apples = items;
  }

  return payload;
};

const reloadAppleList = (dispatch, getState, overrides = {}) => {
  const { pagination } = getState().IDS;
  dispatch(
    loadApple({
      page: pagination?.page ?? 1,
      per_page: pagination?.per_page ?? 10,
      search: pagination?.search ?? "",
      scope: "list",
      silent: true,
      ...overrides,
    })
  );
};

export const loadApple = (params = {}) => (dispatch, getState) => {
  const listState = getState().IDS;
  const { page, per_page, search, scope, silent } = getListParams(
    params,
    listState
  );

  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;

  const req = Api.get("/apple/get", {
    params: { page, per_page, search, scope },
  });

  const onSuccess = (data) => {
    dispatch({
      payload: buildSuccessPayload(data, { search, scope }),
      type: GET_IDS_SUCCESS,
    });
  };

  if (silent) {
    req.then((response) => onSuccess(response)).catch(() => {});
    return;
  }

  toast.promise(req, {
    pending: "Getting Apple IDs...",
    success: {
      render({ data: response }) {
        onSuccess(response);
        return "Your Data Is Ready";
      },
    },
    error: {
      render() {
        return "An error occurred while getting Apple IDs";
      },
    },
  });
};

export const addApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/add", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render({ data }) {
        reloadAppleList(dispatch, getState, { page: 1 });
        return "Apple ID added successfully";
      },
    },
    error: {
      render({ data }) {
        return data?.response.data?.message;
      },
    },
  });
};

export const updateApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/update", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render({ data }) {
        dispatch({
          payload: data.data.data,
          type: UPDATE_IDS_SUCCESS,
        });
        reloadAppleList(dispatch, getState);
        return "Apple ID updated successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while updating your Apple ID";
      },
    },
  });
};

export const deleteApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/delete", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render({ data }) {
        reloadAppleList(dispatch, getState);
        return "Apple ID deleted successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while deleting your Apple ID";
      },
    },
  });
};

export const soldApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/sold", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render({ data }) {
        reloadAppleList(dispatch, getState);
        return "Apple ID updated successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while updating your Apple ID";
      },
    },
  });
};

export const importApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  Api.defaults.headers.common["Content-Type"] = "multipart/form-data";
  let req = Api.post("/apple/import", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadAppleList(dispatch, getState, { page: 1, search: "" });
        return "Apple IDs imported successfully";
      },
    },
    error: {
      render({ data }) {
        return data?.response.data?.message;
      },
    },
  });
};

export const exportApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/export", { number: data.formData.number });

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render(response) {
        reloadAppleList(dispatch, getState, { page: 1 });
        window.open(response.data.data, "_blank");
        return "Apple IDs exported successfully";
      },
    },
    error: {
      render(err) {
        return err?.data.response.data.error;
      },
    },
  });
};

export const exportAppleQr = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/export/qr", {
    number: data.number,
    image: data.image,
    shownote: data.shownote,
    note: data.note,
  });

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render(response) {
        reloadAppleList(dispatch, getState, { page: 1 });
        window.open(response.data.data, "_blank");
        return "Apple IDs exported successfully";
      },
    },
    error: {
      render(err) {
        return err?.data.response.data.error;
      },
    },
  });
};

export const fixApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/fix", { accounts: data.formData.accounts });

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadAppleList(dispatch, getState, { page: 1 });
        return "Apple IDs fixed successfully";
      },
    },
    error: {
      render(err) {
        return err?.data.response.data.error;
      },
    },
  });
};

export const deleteAppleDefective = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/apple/delete/defective", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        dispatch(
          loadApple({
            scope: "defective",
            page: getState().IDS.pagination?.page ?? 1,
            per_page: getState().IDS.pagination?.per_page ?? 10,
            search: getState().IDS.pagination?.search ?? "",
            silent: true,
          })
        );
        return "Apple ID deleted successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while deleting your Apple ID";
      },
    },
  });
};
